import { Check } from 'lucide-react';
import { Link } from 'react-router';

import { Reveal } from '@/components/public/Reveal';
import { Seo } from '@/components/public/Seo';
import { Card, Eyebrow, Lede, Photo, Section, SectionIntro } from '@/components/public/Section';
import { Button } from '@/components/ui/button';
import { SERVICES, SERVICES_PHOTO } from '@/lib/public-content';

/*
 * `/services` — ROADMAP.md §8.5. Copy ported from
 * `reference/prototype-copy/public-site.md`.
 *
 * The six lists here are marketing copy, **not** `property_category` rows. Four
 * of them (Bedrooms, Bathroom, Living Areas, Kitchens) are rooms inside a
 * property rather than kinds of property, which public-site.md is explicit
 * about and which is why seeding them as categories would have been wrong.
 */

export function ServicesPage() {
  return (
    <>
      <Seo title={SERVICES.title} description={SERVICES.description} path="/services" />

      <section aria-labelledby="services-heading" className="relative overflow-hidden bg-ink-50">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-honey-100/70 via-ink-50 to-ink-50"
        />

        <div className="relative mx-auto grid max-w-content items-center gap-12 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>{SERVICES.eyebrow}</Eyebrow>
            <h1
              id="services-heading"
              className="mt-3 text-balance text-[38px] font-bold leading-[1.08] tracking-tight sm:text-[48px]"
            >
              {SERVICES.heading}
            </h1>
            <p className="mt-4 text-[20px] font-semibold text-bark-700">{SERVICES.subheading}</p>
            <Lede>{SERVICES.sub}</Lede>
          </Reveal>

          <Reveal delay={120}>
            <Photo
              src={SERVICES_PHOTO.src}
              alt={SERVICES_PHOTO.alt}
              priority
              imageClassName="aspect-[4/3]"
            />
          </Reveal>
        </div>
      </section>

      {/*
        No heading over the grid, deliberately. The capture's Services page goes
        straight from "Everything your home or business needs, covered" into the
        six lists, and inventing a second heading to sit above them would be
        exactly the paraphrasing §8.5 rules out — I nearly did it.
      */}
      <Section>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.groups.map((group, index) => (
            <Reveal key={group.title} delay={(index % 3) * 80}>
              <Card className="h-full">
                <h2 className="text-h3">{group.title}</h2>
                <ul className="mt-4 space-y-2">
                  {group.items.map((item) => (
                    <li key={item} className="flex gap-2.5 text-body text-ink-700">
                      <Check className="mt-0.5 size-[18px] shrink-0 text-bark-700" aria-hidden />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tint="honey" labelledBy="services-cta">
        <SectionIntro
          id="services-cta"
          heading={SERVICES.closing.heading}
          lede={SERVICES.closing.body}
          align="center"
        />
        <Reveal delay={80} className="text-center">
          <Button asChild size="lg" className="mt-8 shadow-card transition-shadow hover:shadow-pop">
            <Link to="/contact">{SERVICES.closing.cta}</Link>
          </Button>
        </Reveal>
      </Section>
    </>
  );
}
